import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef, NgZone } from '@angular/core';
import { FormGroup, AbstractControl, FormBuilder, Validators, FormControl } from '@angular/forms';
import { Station } from '../../../class/station';
import { StationService } from '../../../service/station/station.service';
import { TranslateService } from '@ngx-translate/core';
import { MapsAPILoader } from '@agm/core';
import { SelectItem, MultiSelectModule, AutoCompleteModule, CalendarModule } from 'primeng/primeng';
import { Marker } from '../../../../../interfaces/marker';
import { NotificationsService } from '../../../../../theme/services/notifications/notifications.service';

@Component({
  selector: 'app-new-station',
  templateUrl: './new-station.component.html',
  styleUrls: ['./new-station.component.scss']
})
export class NewStationComponent implements OnInit {
    @Output() msg: EventEmitter<object> = new EventEmitter<object>();
    @Output() close: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Input() edit: Station;
    public promiseForm: Promise<any>;
    public form: FormGroup;
    public name: AbstractControl;
    public address: AbstractControl;
    public latitude: number;
    public longitude: number;
    public administrative_division: AbstractControl;
    public city: AbstractControl;
    public addressGoogle: string;
    public overlays: any[];
    public selectedPosition: any;
    public holding: AbstractControl;
    public turn: AbstractControl;
    public activated: AbstractControl;
    public police: AbstractControl;
    public distance: AbstractControl;
    public distance_google: AbstractControl;
    public network_support: AbstractControl;
    public subscriber: AbstractControl;
    public employee_id: AbstractControl;
    public employeeSelected: string;
    public filteredEmployees: any[];
    public employeeList: string[] = ['Audi', 'BMW', 'Fiat', 'Ford', 'Honda', 'Jaguar', 'Mercedes', 'Renault', 'Volvo', 'VW'];

    public employeePositionToLoad: string;

    @ViewChild('search') public searchElementRef: ElementRef;
    public zoom: number;
    public marker: Marker;

    public turnOptions: any[];
    public holdingOptions: any[];

    public filteredCurrencies: any[];
    public filteredTimezones: any[];

    public save_exit: boolean;

    constructor(
        fb: FormBuilder,
        public stationService: StationService,
        public translate: TranslateService,
        private ngZone: NgZone,
        private notificationsService: NotificationsService,
        private mapsAPILoader: MapsAPILoader) {

        this.save_exit = false;

        this.employeePositionToLoad = '2';

        this.form = fb.group({
            'name': ['', Validators.compose([Validators.required, Validators.minLength(2)])],
            'address': ['', Validators.compose([Validators.required, Validators.minLength(1)])],
            'administrative_division': ['', Validators.compose([Validators.required, Validators.minLength(1)])],
            'city': ['', Validators.compose([Validators.required, Validators.minLength(1)])],
            'employee_id': [''],
            'holding': [''],
            'turn': [''],
            'police': [''],
            'distance': [''],
            'distance_google': [''],
            'network_support': [''],
            'subscriber': [''],
            'activated': ['', Validators.compose([Validators.required])]
        });

        this.name = this.form.controls['name'];
        this.address = this.form.controls['address'];
        this.activated = this.form.controls['activated'];
        this.administrative_division = this.form.controls['administrative_division'];
        this.holding = this.form.controls['holding'];
        this.turn = this.form.controls['turn'];
        this.police = this.form.controls['police'];
        this.distance = this.form.controls['distance'];
        this.distance_google = this.form.controls['distance_google'];
        this.network_support = this.form.controls['network_support'];
        this.subscriber = this.form.controls['subscriber'];
        this.city = this.form.controls['city'];
        this.employee_id = this.form.controls['employee_id'];

        // set google maps defaults
        this.zoom = 6;
        this.latitude = 39.8282;
        this.longitude = -98.5795;

        this.marker = {
            lat: this.latitude,
            lng: this.longitude,
            label: 'Station',
            draggable: true
        };
    }

    ngOnInit() {
        this.holdingOptions = [];
        this.holdingOptions.push({id: 'holding', name: 'holding'});
        this.holdingOptions.push({id: 'asigned', name: 'asigned'});

        this.turnOptions = [];
        this.turnOptions.push({id: '24/7', name: '24/7'});
        this.turnOptions.push({id: 'none', name: 'none'});

        if (this.edit) {
            this.name.setValue(this.edit.name);
            this.address.setValue(this.edit.address);
            this.activated.setValue(this.edit.activated);
            this.administrative_division.setValue(this.edit.administrative_division);
            this.holding.setValue(this.edit.holding);
            this.turn.setValue(this.edit.turn);
            this.police.setValue(this.edit.police);
            this.distance.setValue(this.edit.distance);
            this.distance_google.setValue(this.edit.distance_google);
            this.network_support.setValue(this.edit.network_support);
            this.subscriber.setValue(this.edit.subscriber);
            this.city.setValue(this.edit.city);
            this.employee_id.setValue(this.edit.employee);
            this.latitude = this.edit.latitude;
            this.longitude = this.edit.longitude;
            if (this.edit.latitude && this.edit.longitude) {
                const lat = this.edit.latitude;
                const lng = this.edit.longitude;
                const position = {
                    lat : lat,
                    lng : lng
                };
                this.setPosition(position);
            }
        } else {
            this.activated.setValue(true);
            this.setCurrentPosition();
        }

        // load Places Autocomplete
        this.mapsAPILoader.load().then(() => {
            const autocomplete = new google.maps.places.Autocomplete(this.searchElementRef.nativeElement, {
                types: ['address']
            });
            autocomplete.addListener('place_changed', () => {
                this.ngZone.run(() => {
                // get the place result
                const place: google.maps.places.PlaceResult = autocomplete.getPlace();

                this.addressGoogle = place.formatted_address;

                if (place.address_components[1]) {
                    this.city.setValue(place.address_components[1].long_name);
                }

                // verify result
                if (place.geometry === undefined || place.geometry === null) {
                    return;
                }

                // set latitude, longitude and zoom
                this.latitude = place.geometry.location.lat();
                this.longitude = place.geometry.location.lng();
                this.zoom = 17;
                });
            });
        });
    }

    public filterEmployees(event) {
        this.filteredEmployees = [];
        for (let i = 0; i < this.employeeList.length; i++) {
            const employee = this.employeeList[i];
            if (employee.toLowerCase().indexOf(event.query.toLowerCase()) === 0) {
                this.filteredEmployees.push(employee);
            }
        }
    }

    private setCurrentPosition() {
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition((position) => {
                this.latitude = position.coords.latitude;
                this.longitude = position.coords.longitude;
                this.zoom = 17;
            });
        }
    }

    private setPosition(position) {
        this.latitude = position.lat;
        this.longitude = position.lng;
        this.zoom = 17;
    }

    public handleMapClick(event) {
        // this.selectedPosition = event.latLng;
        // this.addMarker(this.selectedPosition.lat(), this.selectedPosition.lng());
    }

    public addMarker(lat, lng) {
        const title = (this.name.value !== '') ? this.name.value : 'station';
        this.longitude = lng;
        this.latitude = lat;
        this.overlays = [];
        this.overlays.push(
            new google.maps.Marker({
                position: {
                    lat: lat,
                    lng: lng
                }, title: title}
            )
        );
    }

    public cancelStation () {
        console.log('reset');
        this.form.reset();
        this.close.emit(true);
    }

    public saveandexit (values){
        this.save_exit = true;
        this.newStation(values);
    }

    public newStation(values) {
        if (!this.addressGoogle) {
            this.addressGoogle = values.address;
        }

        const param = {
            'name': values.name,
            'address': this.addressGoogle,
            'latitude': this.latitude,
            'longitude': this.longitude,
            'administrative_division': values.administrative_division,
            'holding': values.holding,
            'turn': values.turn,
            'police': values.police,
            'distance': values.distance,
            'distance_google': values.distance_google,
            'network_support': values.network_support,
            'subscriber': values.subscriber,
            'city': values.city,
            'activated': values.activated,
            'employee_id': values.employee_id.id,
        };
        if (values && this.form.valid) {
            const promise = (this.edit) ?
                this.stationService.updateStation(this.edit.id, param)
                :
                this.stationService.newStation(param);

            this.promiseForm = promise.toPromise().then(
                response => {
                    this.translate.get('saved').subscribe((res: string) => {
                        this.notificationsService.growlNotify('success', res);
                        // this.msg.emit( { severity: 'success', summary: res  } );
                        if (this.save_exit) {
                            this.close.emit(true);
                            this.form.reset();
                            this.save_exit = false;
                        }
                        this.selectedPosition = null;
                    });
                },
                error => {
                    let msg = '';
                    this.translate.get(JSON.parse(error._body).errors[0].detail).subscribe((res1: string) => {
                        this.translate.get(JSON.parse(error._body).errors[1].detail).subscribe((res2: string) => {
                            JSON.parse(error._body).errors.forEach(element => {
                                if (element.source) {
                                    if (element.source.pointer) {
                                        this.translate.get(element.source.pointer).subscribe((res3: string) => {
                                            msg = msg + res3 + ', ';
                                        });
                                    }
                                }
                            });
                            this.notificationsService.growlNotify('error', res1 + ' ' + res2 + ': ' + msg);
                            // this.msg.emit( { severity: 'error', summary: 'Error' } );
                        });
                    });
                }
            );
        }
    }

    markerDragEnd(m: Marker, $event: any) {
        this.latitude = $event.coords.lat;
        this.longitude = $event.coords.lng;
    }
}



// WEBPACK FOOTER //
// D:/inetpub/wwwroot/kaizen-ng/src/app/modules/employee/pages/stations/new-station/new-station.component.ts